import type {
  ColumnOrderState,
  ColumnSizingState,
  ColumnVisibilityState,
  RowSelectionState,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { useTable } from "@tanstack/react-table";
import type { MutableRefObject } from "react";
import { useCallback, useMemo } from "react";

import type { DispatchViewStatePatch, ResolvedDispatchView } from "@storyboard/dispatch-view/resolve";
import type { DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import { acknowledgementDotColumn, buildDispatchColumns, labelForColumnId } from "@/components/Dispatch/columns";
import type { TagDefinition } from "@/types/driver";
import { buildCheckboxColumn } from "@/ui/DataTable";
import { type DataTableInstance, dataTableFeatures } from "@/ui/dataTableFeatures";
import type { DispatchTableRow } from "./dispatchTableRow";

function resolveUpdater<T>(updater: Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

/**
 * The entries of `next` that differ from `current`: what the member changed, and nothing the layer beneath said.
 */
function changedEntries<T>(next: Record<string, T>, current: Record<string, T>): Record<string, T> {
  return Object.fromEntries(Object.entries(next).filter(([key, value]) => current[key] !== value));
}

/**
 * The table from the resolved view: its column definitions, every piece of column state TanStack keeps, and a writer
 * for each, wrapped in the table instance the grid and the Display popover render.
 *
 * The table's own two columns lead every row, the acknowledgement dot and the checkbox that ticks rows for bulk
 * actions, then the offered columns, the view's first. Visibility, order, sizing, and pinning are read from the
 * resolved layout and written back as `layout.columns` patches on the view's state; a TanStack updater is resolved
 * against the resolved value and only the entries it changed are written, so one hidden column stores one boolean,
 * not one per offered column. A column shown and hidden again stores an explicit value equal to the view's: it reads
 * as no override, since overrides are computed by comparison, but it pins that column against a later change to the
 * view, which is the price of writes that never read the layer beneath. The sort rides along in TanStack's shape only
 * so each header can draw its arrow; the rows are sorted by the query, not the table, and header clicks open the
 * column menu rather than toggling a table sort. Every row renders: the shared feature set registers pagination, so
 * the table opts out.
 */
export function useDispatchTableLayout({
  definition,
  resolved,
  tagsBySlug,
  now,
  tableRows,
  ticks,
  onTicksChange,
  filteredColumnIds,
  shiftSelectRef,
  write,
  badgeCap,
}: {
  definition: DispatchViewDefinition;
  resolved: ResolvedDispatchView;
  tagsBySlug: ReadonlyMap<string, TagDefinition>;
  now: number;
  /** The rows the query yields, in the order the table shows them. */
  tableRows: DispatchTableRow[];
  /** The ticked rows, kept by the page and handed to the table as its row selection. */
  ticks: RowSelectionState;
  onTicksChange: (updater: Updater<RowSelectionState>) => void;
  /** The columns an ad-hoc condition narrows on; their headers carry the filter mark. */
  filteredColumnIds: ReadonlySet<string>;
  /** The row last ticked on its own, which a Shift-click on a checkbox extends from. */
  shiftSelectRef: MutableRefObject<string | null>;
  /** Where every layout change goes, as a patch on the view's state. */
  write: (patch: DispatchViewStatePatch) => void;
  /** Where a driver row's unacknowledged count saturates. */
  badgeCap: number;
}): {
  table: DataTableInstance<DispatchTableRow>;
  columnPinning: { left: string[] };
  onColumnPinningChange: (left: string[]) => void;
  togglePin: (columnId: string) => void;
  hide: (columnId: string) => void;
  labelFor: (columnId: string) => string;
  viewColumnIds: ReadonlySet<string>;
} {
  const { columns: offered, visibility, order, sizing, pinning } = resolved.layout;
  const { sort } = resolved.query;

  const columns = useMemo(
    () => [
      acknowledgementDotColumn(badgeCap),
      buildCheckboxColumn<DispatchTableRow>({ shiftSelectRef }),
      ...buildDispatchColumns(offered, tagsBySlug, now, filteredColumnIds),
    ],
    [offered, tagsBySlug, now, filteredColumnIds, shiftSelectRef, badgeCap],
  );
  const viewColumnIds = useMemo(() => new Set(definition.layout.columns.map((column) => column.field)), [definition]);
  const sorting = useMemo<SortingState>(
    () => (sort ? [{ id: sort.field, desc: sort.direction === "desc" }] : []),
    [sort],
  );

  // The table hands back whole maps; only the keys that changed go to the member's layer, so an inherited default
  // stays inherited rather than being copied in as the member's own.
  const onColumnVisibilityChange = useCallback(
    (updater: Updater<ColumnVisibilityState>) => {
      const changed = changedEntries(resolveUpdater(updater, visibility), visibility);
      if (Object.keys(changed).length > 0) write({ layout: { columns: { visibility: changed } } });
    },
    [write, visibility],
  );
  const onColumnOrderChange = useCallback(
    (updater: Updater<ColumnOrderState>) => write({ layout: { columns: { order: resolveUpdater(updater, order) } } }),
    [write, order],
  );
  const onColumnSizingChange = useCallback(
    (updater: Updater<ColumnSizingState>) => {
      const changed = changedEntries(resolveUpdater(updater, sizing), sizing);
      if (Object.keys(changed).length > 0) write({ layout: { columns: { sizing: changed } } });
    },
    [write, sizing],
  );
  const onColumnPinningChange = useCallback(
    (left: string[]) => write({ layout: { columns: { pinning: { left } } } }),
    [write],
  );
  const togglePin = useCallback(
    (columnId: string) =>
      onColumnPinningChange(
        pinning.left.includes(columnId) ? pinning.left.filter((id) => id !== columnId) : [...pinning.left, columnId],
      ),
    [onColumnPinningChange, pinning],
  );
  const hide = useCallback(
    (columnId: string) => write({ layout: { columns: { visibility: { [columnId]: false } } } }),
    [write],
  );
  const labelFor = useCallback(
    (columnId: string) => labelForColumnId(columnId, definition, tagsBySlug),
    [definition, tagsBySlug],
  );

  const table = useTable({
    features: dataTableFeatures,
    data: tableRows,
    columns,
    state: { columnVisibility: visibility, columnOrder: order, columnSizing: sizing, sorting, rowSelection: ticks },
    enableRowSelection: true,
    onRowSelectionChange: onTicksChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnSizingChange,
    getRowId: (row) => row.id,
    manualPagination: true,
    enableSorting: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
  });

  return { table, columnPinning: pinning, onColumnPinningChange, togglePin, hide, labelFor, viewColumnIds };
}
